import React, { useState, useEffect } from 'react';
import { Card, CardBody, Button, Collapse } from 'reactstrap';
import { FiChevronDown, FiChevronUp, FiCheck } from 'react-icons/fi';
import { connect } from 'react-redux';
import { getApiByFriendlyName } from '../../../redux/actions/apis';
import { showStripeModal, showStripeFreeModal } from '../../../redux/actions/stripeModal';
import StripeSignupOrSwitchModal from '../../Apis/components/StripeSignupOrSwitchModal';

const PlanSelection = ({ 
  onPlanSelected, 
  onTrialTierLoaded,
  allPlansRef,
  apis, 
  getApiByFriendlyName, 
  showStripeModal, 
  showStripeFreeModal,
  stripeModalVisible,
  stripeModalTier,
  plan
}) => {
  const [showAllPlans, setShowAllPlans] = useState(false);
  const [developerTiers, setDeveloperTiers] = useState([]);
  const [trialTier, setTrialTier] = useState(null);

  // Load API data to get tiers
  useEffect(() => {
    if (!apis.detailApi.id) {
      getApiByFriendlyName('Opengraphio');
    }
  }, [apis.detailApi.id, getApiByFriendlyName]);

  // Process tiers when API data is loaded
  useEffect(() => {
    if (apis.detailApi.tiers) {
      // Filter for developer tiers (not marketing)
      const devTiers = apis.detailApi.tiers.filter(tier => 
        tier.isMarketing === false && !tier.isDiscontinued && !tier.isExperimental
      );
      const sortedTiers = devTiers.sort((a, b) => a.priceMonthly - b.priceMonthly);
      setDeveloperTiers(sortedTiers);

      // Find the trial tier
      const trial = devTiers.find(tier => tier.isTrial);
      setTrialTier(trial);
      if (onTrialTierLoaded) {
        onTrialTierLoaded(trial); // Call the new prop
      }
    }
  }, [apis.detailApi.tiers, onTrialTierLoaded]);

  // Auto-scroll to plans when they're shown
  useEffect(() => {
    if (showAllPlans && allPlansRef && allPlansRef.current) {
      // Small delay to allow the collapse animation to start
      setTimeout(() => {
        allPlansRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 150);
    }
  }, [showAllPlans, allPlansRef]);

  const toggleAllPlans = () => {
    setShowAllPlans(!showAllPlans);
  };

  const handlePlanSelect = (planType, tier = null) => {
    if (!tier) {
      // Handle case where no tier is provided
      onPlanSelected(planType, tier);
      return;
    }

    // For trial plans or paid plans, show the Stripe modal
    if (tier.isTrial || tier.priceMonthly > 0) {
      showStripeModal(tier);
    } else if (tier.priceMonthly === 0) {
      // For truly free plans (not trial), show free modal

      // Complete onboarding after free plan selection
      setTimeout(() => {
        onPlanSelected(planType, tier);
      }, 100);
    }
  };

  // Parse trial tier features from description
  const getTrialFeatures = () => {
    if (!trialTier || !trialTier.description) {
      return [
        '25,000 requests during trial',
        'Full API access',
        'Proxy support',
        'Priority performance'
      ];
    }
    
    // Split description by | and filter out marketing features (starting with -)
    const features = trialTier.description.split('|')
      .filter(feature => !feature.trim().startsWith('-'))
      .filter(feature => !feature.trim().startsWith('x'))
      .map(feature => feature.trim());
    
    return features.length > 0 ? features : [
      '25,000 requests during trial',
      'Full API access',
      'Proxy support', 
      'Priority performance'
    ];
  };

  // Get non-trial tiers for comparison
  const getComparisionTiers = () => {
    return developerTiers.filter(tier => !tier.isTrial && !tier.trialTierId)
  };

  return (
    <div className="plan-selection">
      <StripeSignupOrSwitchModal 
        source={plan.stripeSource} 
        tier={stripeModalTier} 
        visible={stripeModalVisible}
        plan={plan}
      />
      
      <div className="plan-selection-header">
        <h2>Choose your plan</h2>
      </div>

      <div className="plan-selection-content">
        {/* Free Trial Block - Centered */}
        <Card className="free-trial-card">
          <CardBody className="text-center">
            <div className="trial-badge">
              <span className="trial-icon">🟩</span>
            </div>
            <h3 className="trial-title">
              {trialTier ? 'Developer 7-Day Free Trial' : null}
            </h3>
            <p className="trial-description">
              {trialTier ? 
                'Test all Pro features for 7 days - then $25/month:' : 
                'Test all Pro features for 7 days:'
              }
            </p>
            
            <ul className="trial-features">
              {getTrialFeatures().map((feature, index) => (
                <li key={index} className="trial-feature">
                  <FiCheck className="feature-check" />
                  {feature}
                </li>
              ))}
            </ul>
            
            <Button 
              color="success" 
              size="lg" 
              className="trial-cta-button"
              onClick={() => handlePlanSelect('free-trial', trialTier)}
            >
              Start Free Trial →
            </Button>
          </CardBody>
        </Card>

        {/* Show All Plans Button */}
        <div className="show-all-plans-section">
          <Button
            color="outline-primary"
            size="lg"
            className="show-all-plans-btn"
            onClick={toggleAllPlans}
          >
            {showAllPlans ? 'Hide Plans' : 'Show All Plans'}
          </Button>
        </div>

        {/* Expandable Plans Section */}
        <div className="all-plans-section" ref={allPlansRef}>
          <Collapse isOpen={showAllPlans}>
            <div className="all-plans-grid">
              {getComparisionTiers().map((tier, index) => (
                <Card key={index} className="plan-card">
                  <CardBody>
                    <div className="plan-info">
                      <h4 className="plan-name">{tier.name}</h4>
                      {tier.pricingScheme && tier.pricingScheme.steps && tier.pricingScheme.steps[0] && (
                        <div className="plan-credits">
                          { tier.pricingScheme.steps[0].ceiling.toLocaleString()} requests/month
                        </div>
                      )}
                      {tier.rateLimits && tier.rateLimits[0] && (
                        <div className="plan-credits">
                          {tier.rateLimits[0].quantity} requests/month
                        </div>
                      )}
                    </div>
                    
                    <div className="plan-price-column">
                      <div className="plan-price">
                        {tier.priceMonthly === 0 ? 'Free' : `$${tier.priceMonthly}`}
                        {tier.priceMonthly !== 0 && <span className="price-period">/mo</span>}
                      </div>
                    </div>
                    
                    <div className="plan-action">
                      <Button 
                        color="primary"
                        size="sm"
                        className="plan-select-btn"
                        onClick={() => handlePlanSelect(tier.name.toLowerCase(), tier)}
                      >
                        Choose
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>
          </Collapse>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  apis: state.apis,
  stripeModalVisible: state.stripeModal.visible,
  stripeModalTier: state.stripeModal.tier,
  plan: state.plans.detailPlan,
});

const mapDispatchToProps = {
  getApiByFriendlyName,
  showStripeModal,
  showStripeFreeModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(PlanSelection); 